import { Feature, Map } from 'ol';
import { Point } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { action, computed, makeObservable, observable } from 'mobx';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import image from '@assets/images/user-layer.svg';
import { Vector as VectorLayer } from 'ol/layer';
import {
  IUserLayerRecord,
  IUserLayerRecordGeometry,
} from '@project/API/resources/userLayer';

export class UserLayer {
  @observable private _isVisible = false;
  @observable private _id: number;

  private _source: VectorSource<Feature<Point>> = new VectorSource();
  private _style = new Style({
    image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.05 }),
  });
  private _layer: VectorLayer = new VectorLayer({
    source: this._source,
    style: this._style,
    zIndex: 999,
  });

  constructor(private _map: Map, id: number) {
    this._id = id;
    makeObservable<UserLayer>(this);
  }

  @computed get isVisible(): boolean {
    return this._isVisible;
  }

  @computed get id(): number {
    return this._id;
  }

  hasFeature = (feature: Feature<Point>) => {
    return this._source.hasFeature(feature);
  };

  addFeatures = (value: IUserLayerRecord[]) => {
    const features = value.map((v) => this._serializeUserLayerRecord(v));
    this._source.addFeatures(features);
  };

  addFeature = (value: IUserLayerRecord) => {
    const feature = this._serializeUserLayerRecord(value);
    this._source.addFeature(feature);
  };

  removeFeature = (id: number) => {
    const feature = this._source.getFeatureById(id);

    if (feature) this._source.removeFeature(feature);
  };

  clearFeatures = () => {
    this._source.clear();
  };

  editFeature = (value: IUserLayerRecord) => {
    const feature = this._source.getFeatureById(value.id);

    if (!feature) this.addFeature(value);
    else {
      feature.setProperties(value.properties);

      if (!value.geometry) feature.setGeometry(undefined);
      else
        feature.setGeometry(
          this._serializeUserLayerRecordGeometry(value.geometry),
        );
    }
  };

  @action show = () => {
    if (!this._isVisible) {
      this._map.addLayer(this._layer);
    }

    this._isVisible = true;
  };

  @action hide = () => {
    this._isVisible = false;
    this._map.removeLayer(this._layer);
  };

  private _serializeUserLayerRecord = (
    value: IUserLayerRecord,
  ): Feature<Point> => {
    const coordinates = value.geometry?.coordinates;

    const feature = new Feature<Point>({
      geometry: coordinates ? new Point(coordinates) : undefined,
    });

    feature.setId(value.id);
    feature.setProperties(value.properties);

    return feature;
  };

  private _serializeUserLayerRecordGeometry = (
    value: IUserLayerRecordGeometry,
  ): Point => {
    return new Point(value.coordinates);
  };
}
